import { z } from 'zod';

import { HotelCancellationBandSchema } from './hotel-cancellation-policy.js';

/**
 * Post-booking cancellation, shared by the flight and hotel funnels.
 *
 * One contract for both products on purpose. The supplier calls differ (Air uses
 * `SendChangeRequest` + `GetChangeRequestStatus`; the TBO Holidays HotelAPI uses
 * `CancelBooking` and is read back through `BookingDetail`) but everything the
 * traveller sees is identical: a quote, a reason, a request that takes time, and
 * an answer. Two parallel implementations of that would drift, and the screen
 * that drifted would be the one shown to someone whose money is in play.
 *
 * ## The rule this file encodes
 *
 * **A cancellation is a request, not an action.** TBO answering "received"
 * is not TBO answering "done" — a change request can sit `Pending` for hours and
 * can still come back `Rejected`. So the booking is never marked cancelled on
 * the strength of the request being accepted; it moves to a separate
 * `CANCELLATION_REQUESTED` state and only a terminal supplier status resolves
 * it. Marking it cancelled early is how a customer is told their flight is gone
 * while they are still, in fact, ticketed on it.
 *
 * ## What is deliberately NOT here
 *
 * The customer refund leg. Cancelling with TBO credits the *agency wallet*; it
 * does not move money back to the traveller's card. That is a separate operation
 * against the payment gateway with its own independent failure modes, and it is
 * not implemented yet. `refundPaise` below is therefore **the amount TBO says it
 * is returning to us**, and every surface that renders it must say that the
 * payout is arranged separately rather than implying it is already on its way.
 */

// ---- Reasons ------------------------------------------------------------

/**
 * Why the traveller is cancelling.
 *
 * `Remarks` is mandatory on TBO's change request and is what a TBO agent reads
 * when a cancellation is disputed weeks later, so it cannot be a hardcoded
 * placeholder. A closed list rather than free text: the codes are stable enough
 * to report on, and the free-text `note` beside them carries anything the list
 * does not cover.
 */
export const CANCELLATION_REASONS = [
  { code: 'PLANS_CHANGED', label: 'My travel plans changed' },
  { code: 'BOOKED_BY_MISTAKE', label: 'I booked this by mistake' },
  { code: 'FOUND_ALTERNATIVE', label: 'I found a better option' },
  { code: 'EMERGENCY', label: 'Medical or family emergency' },
  { code: 'DOCUMENT_ISSUE', label: 'Visa or document problem' },
  { code: 'SUPPLIER_SCHEDULE_CHANGE', label: 'The airline or hotel changed my booking' },
  { code: 'OTHER', label: 'Something else' },
] as const;

export const CancellationReasonCodeSchema = z.enum([
  'PLANS_CHANGED',
  'BOOKED_BY_MISTAKE',
  'FOUND_ALTERNATIVE',
  'EMERGENCY',
  'DOCUMENT_ISSUE',
  'SUPPLIER_SCHEDULE_CHANGE',
  'OTHER',
]);
export type CancellationReasonCode = z.infer<typeof CancellationReasonCodeSchema>;

/** Label for a reason code. Falls back to the code so an unknown value is still readable. */
export function cancellationReasonLabel(code: string): string {
  return CANCELLATION_REASONS.find((r) => r.code === code)?.label ?? code;
}

// ---- The state the UI renders ------------------------------------------

/**
 * What the traveller can do about cancelling, right now.
 *
 * Derived server-side from the booking status, the open change request and the
 * travel dates — never assembled in the browser. There is one place that decides
 * whether a cancel button appears, and the same answer drives the button, the
 * banner and the API guard, so they cannot disagree.
 */
export const TravelCancellationStateSchema = z.enum([
  /** Not a confirmed booking. No cancellation affordance at all. */
  'NOT_APPLICABLE',
  /** Confirmed and cancellable. The button is live. */
  'AVAILABLE',
  /** Confirmed but we will not cancel automatically — see `blockedReason`. */
  'BLOCKED',
  /** Sent to TBO, no terminal answer yet. The button is inert. */
  'REQUESTED',
  /** TBO confirmed the cancellation. Terminal. */
  'CANCELLED',
  /** TBO refused it. The traveller may try again; see `rejectionReason`. */
  'REJECTED',
]);
export type TravelCancellationState = z.infer<typeof TravelCancellationStateSchema>;

const MinorUnits = z.string().regex(/^-?\d+$/);

// ---- Scope --------------------------------------------------------------

/**
 * Whether a cancellation covers the whole booking or named travellers.
 *
 * `PARTIAL` is flight-only, and not by omission: the TBO Holidays HotelAPI
 * addresses `CancelBooking` by booking, with no room- or guest-level selector,
 * so a hotel cancellation is all-or-nothing at the supplier and offering
 * anything else would be a promise we cannot keep.
 *
 * The distinction matters beyond the payload. A partial cancellation leaves the
 * booking **CONFIRMED and still flying** with fewer people on it, so the booking
 * status cannot record what happened — only the passenger rows can.
 */
export const CancellationScopeSchema = z.enum(['FULL', 'PARTIAL']);
export type CancellationScope = z.infer<typeof CancellationScopeSchema>;

/**
 * A traveller as the cancellation dialog needs to show them.
 *
 * `isCancellable` is computed server-side rather than inferred in the browser
 * from the other fields, so the checkbox and the API guard cannot disagree.
 */
export const CancellablePassengerSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  paxType: z.string(),
  ticketNumber: z.string().nullable().default(null),
  /** Already cancelled on an earlier partial request. */
  isCancelled: z.boolean().default(false),
  /**
   * False when this traveller cannot be cancelled on their own — no supplier
   * ticket id (bookings made before we captured it), or already cancelled.
   */
  isCancellable: z.boolean().default(false),
});
export type CancellablePassenger = z.infer<typeof CancellablePassengerSchema>;

/**
 * The cancellation block carried on every booking session view.
 *
 * Always present — `NOT_APPLICABLE` rather than a null block — so the confirmation
 * page never has to distinguish "no cancellation data" from "cannot cancel".
 */
export const TravelCancellationSchema = z.object({
  state: TravelCancellationStateSchema,
  /**
   * Why cancelling is unavailable, in the traveller's words. Non-null only when
   * `state` is `BLOCKED`.
   */
  blockedReason: z.string().nullable().default(null),
  /** TBO's own words when it refused. Non-null only when `state` is `REJECTED`. */
  rejectionReason: z.string().nullable().default(null),
  /** ISO timestamp the request was raised. Null before that. */
  requestedAt: z.string().nullable().default(null),
  /** ISO timestamp TBO gave a terminal answer. Null until then. */
  resolvedAt: z.string().nullable().default(null),
  /**
   * What was quoted to the traveller when they confirmed. Kept alongside the
   * settled figure so a variance is visible rather than silently overwritten.
   */
  quotedChargePaise: MinorUnits.nullable().default(null),
  /** What TBO actually charged. Null until the request resolves. */
  chargePaise: MinorUnits.nullable().default(null),
  /**
   * What TBO is returning **to the agency**, not to the traveller's card. The
   * customer payout is a separate operation that is not implemented yet; every
   * surface rendering this must say so.
   */
  refundPaise: MinorUnits.nullable().default(null),
  currencyCode: z.string().default('INR'),
  /**
   * True once polling gave up and a human owns the request. The traveller is
   * told we are chasing it manually rather than being left watching a spinner
   * that will never resolve.
   */
  isEscalated: z.boolean().default(false),

  // ---- partial cancellation
  /** What the most recent request covered. */
  scope: CancellationScopeSchema.default('FULL'),
  /**
   * Whether this booking can be cancelled traveller-by-traveller *right now*.
   *
   * Requires a multi-passenger flight where at least two people are still live
   * and every one of them carries a supplier ticket id. False for hotels
   * always, and false for flights booked before that id was captured.
   */
  canCancelPassengers: z.boolean().default(false),
  /** The travellers, when `canCancelPassengers` is true. Empty otherwise. */
  passengers: z.array(CancellablePassengerSchema).default([]),
  /** Names covered by an in-flight or resolved partial request, for messaging. */
  requestedPassengerNames: z.array(z.string()).default([]),
});
export type TravelCancellation = z.infer<typeof TravelCancellationSchema>;

// ---- Quote --------------------------------------------------------------

/**
 * What cancelling costs, shown before the traveller commits.
 *
 * Sourced differently per product and that difference is deliberate:
 *
 *  - **Hotel** reads the policy snapshot pinned at PreBook. Not a live supplier
 *    quote — the pinned policy is what the guest agreed to, and re-quoting would
 *    let a supplier revise the terms after the fact.
 *  - **Flight** calls `GetCancellationCharges`, because the Air API publishes one
 *    and the airline's number is authoritative. Carrier support varies; when the
 *    carrier will not quote, `isChargeKnown` is false.
 */
export const TravelCancellationQuoteSchema = z.object({
  chargePaise: MinorUnits,
  /** Expected agency credit. See the note on `TravelCancellationSchema.refundPaise`. */
  refundPaise: MinorUnits,
  currencyCode: z.string(),
  /**
   * False when the charge could not be computed or the carrier would not quote
   * one. The confirm action must be blocked in that case: an unknown charge is
   * not a free cancellation, and quoting a refund we cannot honour is worse than
   * refusing to quote at all.
   */
  isChargeKnown: z.boolean().default(true),
  /** Hotel only: which band of the pinned policy is in force. Null for flights. */
  band: HotelCancellationBandSchema.nullable().default(null),
  /** Supplier prose, when there is any. */
  note: z.string().nullable().default(null),
});
export type TravelCancellationQuote = z.infer<typeof TravelCancellationQuoteSchema>;

// ---- Request ------------------------------------------------------------

export const CancelTravelBookingInputSchema = z.object({
  reasonCode: CancellationReasonCodeSchema,
  /**
   * `TravelBookingPassenger.id`s to cancel. Omit or leave empty for the whole
   * booking.
   *
   * Naming *every* live traveller is not a partial cancellation and is not
   * treated as one — the server routes it to the full path, because leaving a
   * booking alive with nobody on it is a state neither we nor the airline has a
   * use for.
   */
  passengerIds: z.array(z.string()).max(9).default([]),
  /** Optional detail. Appended to the TBO `Remarks` string. */
  note: z.string().trim().max(300).nullable().default(null),
  /**
   * The charge figure the traveller was shown when they pressed confirm.
   *
   * The server recomputes and refuses the request if the two disagree. This is
   * the guard for the narrow but real window where a free-cancellation deadline
   * lapses between the dialog opening and the traveller confirming: without it,
   * someone who read "no charge" is silently charged the next band.
   */
  acknowledgedChargePaise: MinorUnits,
});
export type CancelTravelBookingInput = z.infer<typeof CancelTravelBookingInputSchema>;

/**
 * The answer to a cancel request.
 *
 * Returns the whole cancellation block rather than a bare status so the caller
 * can render the new state from one payload without a follow-up read — and so
 * there is exactly one shape describing "where this cancellation stands".
 */
export const CancelTravelBookingResultSchema = z.object({
  /** `REQUESTED` in the ordinary case; `CANCELLED` when the supplier resolved inline. */
  cancellation: TravelCancellationSchema,
  /** TBO's handle on the request. Null when the supplier did not return one. */
  changeRequestId: z.string().nullable().default(null),
});
export type CancelTravelBookingResult = z.infer<typeof CancelTravelBookingResultSchema>;

/** The block as it reads on a booking that was never cancellable. */
export const NOT_APPLICABLE_CANCELLATION: TravelCancellation = {
  state: 'NOT_APPLICABLE',
  blockedReason: null,
  rejectionReason: null,
  requestedAt: null,
  resolvedAt: null,
  quotedChargePaise: null,
  chargePaise: null,
  refundPaise: null,
  currencyCode: 'INR',
  isEscalated: false,
  scope: 'FULL',
  canCancelPassengers: false,
  passengers: [],
  requestedPassengerNames: [],
};
